import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Avatar,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    alpha,
    Chip,
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    TrendingUp as TrendingUpIcon,
    EmojiEvents as TrophyIcon,
    QuestionAnswer as QuestionIcon,
    Refresh as RefreshIcon,
    Timeline as TimelineIcon,
    AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from './AuthContext';

const QuestionStats = () => {
    const [stats, setStats] = useState({
        totalQuestionsAnswered: 0,
        totalCorrectAnswers: 0,
        totalIncorrectAnswers: 0,
        accuracy: 0,
        lastQuestionAnsweredAt: null,
        userName: '',
        totalTimeSpent: 0,
        formattedTimeSpent: '0m',
        lastActivityAt: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const { user, checkTokenExpiration } = useAuth();

    useEffect(() => {
        if (user) {
            fetchStats();
        }
    }, [user]);

    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!checkTokenExpiration()) {
                throw new Error('Session expired');
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/users/my-stats`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            setStats(response.data.data);
        } catch (err) {
            console.error('Error fetching stats:', err);
            setError(err.response?.data?.message || 'Failed to fetch statistics');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchStats();
    };

    const handleResetStats = async () => {
        try {
            setLoading(true);
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/users/reset-stats`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );
            await fetchStats();
            setResetDialogOpen(false);
        } catch (err) {
            console.error('Error resetting stats:', err);
            setError(err.response?.data?.message || 'Failed to reset statistics');
            setLoading(false);
        }
    };

    const MetricCard = ({ title, value, icon, color, subtitle, percentage }) => (
        <Card
            sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
                backdropFilter: 'blur(20px)',
                border: `2px solid ${alpha(color, 0.2)}`,
                borderRadius: 3,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 40px ${alpha(color, 0.2)}`,
                    border: `2px solid ${alpha(color, 0.4)}`,
                },
            }}
        >
            <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Avatar
                        sx={{
                            bgcolor: alpha(color, 0.15),
                            color: color,
                            width: 70,
                            height: 70,
                            border: `3px solid ${alpha(color, 0.3)}`,
                        }}
                    >
                        {icon}
                    </Avatar>
                    {percentage !== undefined && (
                        <Chip
                            label={`${percentage}%`}
                            sx={{
                                bgcolor: alpha(color, 0.15),
                                color: color,
                                fontWeight: 700,
                                fontSize: '1rem',
                                height: 40,
                                px: 2,
                            }}
                        />
                    )}
                </Box>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 900,
                        color: color,
                        mb: 1,
                        fontSize: '3rem',
                        textShadow: `0 2px 10px ${alpha(color, 0.2)}`,
                    }}
                >
                    {value}
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        color: alpha('#1a1a1a', 0.8),
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: '1.1rem',
                    }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: alpha('#64748b', 0.8),
                            fontSize: '0.9rem',
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );

    if (loading && !refreshing) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress sx={{ color: '#ff5e17' }} size={60} />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={5}>
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 900,
                            background: 'linear-gradient(135deg, #1e293b 0%, #475569 50%, #ff5e17 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1,
                        }}
                    >
                        My Practice Statistics
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: alpha('#64748b', 0.8),
                            fontWeight: 500,
                        }}
                    >
                        Track your overall progress across all practice questions
                    </Typography>
                </Box>
                <Box display="flex" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={refreshing}
                        sx={{
                            borderColor: '#ff5e17',
                            color: '#ff5e17',
                            borderWidth: 2,
                            '&:hover': {
                                borderColor: '#ff5e17',
                                backgroundColor: alpha('#ff5e17', 0.05),
                                borderWidth: 2,
                            },
                        }}
                    >
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setResetDialogOpen(true)}
                        sx={{
                            borderWidth: 2,
                            '&:hover': {
                                borderWidth: 2,
                            },
                        }}
                    >
                        Reset Stats
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 4, borderRadius: 3, fontSize: '1rem' }}>
                    {error}
                </Alert>
            )}

            {/* Main Statistics Cards */}
            <Grid container spacing={4} mb={5}>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Total Questions"
                        value={stats.totalQuestionsAnswered.toLocaleString()}
                        icon={<QuestionIcon sx={{ fontSize: 40 }} />}
                        color="#3b82f6"
                        subtitle="Questions answered"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Correct Answers"
                        value={stats.totalCorrectAnswers.toLocaleString()}
                        icon={<CheckCircleIcon sx={{ fontSize: 40 }} />}
                        color="#10b981"
                        subtitle="Questions you got right"
                        percentage={stats.accuracy}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Incorrect Answers"
                        value={stats.totalIncorrectAnswers.toLocaleString()}
                        icon={<CancelIcon sx={{ fontSize: 40 }} />}
                        color="#ef4444"
                        subtitle="Questions you missed"
                        percentage={stats.totalQuestionsAnswered > 0 ? Math.round((stats.totalIncorrectAnswers / stats.totalQuestionsAnswered) * 100) : 0}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard
                        title="Time Spent"
                        value={stats.formattedTimeSpent}
                        icon={<AccessTimeIcon sx={{ fontSize: 40 }} />}
                        color="#8b5cf6"
                        subtitle="Total practice time"
                    />
                </Grid>
            </Grid>

            {/* Accuracy & Progress Section */}
            <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                    <Card
                        sx={{
                            background: 'linear-gradient(135deg, rgba(255,94,23,0.05) 0%, rgba(255,255,255,0.95) 100%)',
                            border: `2px solid ${alpha('#ff5e17', 0.2)}`,
                            borderRadius: 3,
                            height: '100%',
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Box display="flex" alignItems="center" gap={2} mb={3}>
                                <Avatar
                                    sx={{
                                        bgcolor: alpha('#ff5e17', 0.15),
                                        color: '#ff5e17',
                                        width: 60,
                                        height: 60,
                                    }}
                                >
                                    <TrendingUpIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                        Overall Accuracy
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: alpha('#64748b', 0.8) }}>
                                        Your performance across all questions
                                    </Typography>
                                </Box>
                            </Box>

                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="h2" sx={{ fontWeight: 900, color: '#ff5e17' }}>
                                        {stats.accuracy}%
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            color: stats.accuracy >= 70 ? '#10b981' : '#ef4444',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {stats.accuracy >= 90 ? '🏆 Excellent!' : stats.accuracy >= 80 ? '🎯 Great!' : stats.accuracy >= 70 ? '👍 Good' : '📚 Keep Practicing!'}
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={stats.accuracy}
                                    sx={{
                                        height: 20,
                                        borderRadius: 10,
                                        backgroundColor: alpha('#e0e0e0', 0.3),
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: stats.accuracy >= 70 ? '#10b981' : '#ff5e17',
                                            borderRadius: 10,
                                            backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.2) 75%, transparent 75%, transparent)',
                                            backgroundSize: '20px 20px',
                                            animation: 'progress-bar-stripes 1s linear infinite',
                                        },
                                    }}
                                />
                            </Box>

                            <Grid container spacing={3} mt={2}>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            bgcolor: alpha('#10b981', 0.1),
                                            p: 2,
                                            borderRadius: 2,
                                            border: `2px solid ${alpha('#10b981', 0.2)}`,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600, mb: 0.5 }}>
                                            Success Rate
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#10b981' }}>
                                            {stats.totalQuestionsAnswered > 0 ? Math.round((stats.totalCorrectAnswers / stats.totalQuestionsAnswered) * 100) : 0}%
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            bgcolor: alpha('#3b82f6', 0.1),
                                            p: 2,
                                            borderRadius: 2,
                                            border: `2px solid ${alpha('#3b82f6', 0.2)}`,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ color: '#3b82f6', fontWeight: 600, mb: 0.5 }}>
                                            Questions Per Correct
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#3b82f6' }}>
                                            {stats.totalCorrectAnswers > 0 ? (stats.totalQuestionsAnswered / stats.totalCorrectAnswers).toFixed(1) : 0}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        sx={{
                            background: 'linear-gradient(135deg, rgba(59,130,246,0.05) 0%, rgba(255,255,255,0.95) 100%)',
                            border: `2px solid ${alpha('#3b82f6', 0.2)}`,
                            borderRadius: 3,
                            height: '100%',
                        }}
                    >
                        <CardContent sx={{ p: 4 }}>
                            <Box display="flex" alignItems="center" gap={2} mb={3}>
                                <Avatar
                                    sx={{
                                        bgcolor: alpha('#3b82f6', 0.15),
                                        color: '#3b82f6',
                                        width: 60,
                                        height: 60,
                                    }}
                                >
                                    <TimelineIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                                        Activity
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: alpha('#64748b', 0.8) }}>
                                        Last answered
                                    </Typography>
                                </Box>
                            </Box>

                            {stats.lastQuestionAnsweredAt ? (
                                <>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#3b82f6', mb: 1 }}>
                                        {format(new Date(stats.lastQuestionAnsweredAt), 'MMM dd, yyyy')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: alpha('#64748b', 0.8), mb: 3 }}>
                                        at {format(new Date(stats.lastQuestionAnsweredAt), 'h:mm a')}
                                    </Typography>
                                </>
                            ) : (
                                <Typography variant="body1" sx={{ color: alpha('#64748b', 0.8), mb: 3 }}>
                                    No activity yet
                                </Typography>
                            )}

                            <Box
                                sx={{
                                    bgcolor: alpha('#8b5cf6', 0.1),
                                    p: 2,
                                    borderRadius: 2,
                                    border: `2px solid ${alpha('#8b5cf6', 0.2)}`,
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#8b5cf6', fontWeight: 600, mb: 1 }}>
                                    Keep it up!
                                </Typography>
                                <Typography variant="body2" sx={{ color: alpha('#64748b', 0.9) }}>
                                    {stats.totalQuestionsAnswered >= 100
                                        ? '🌟 You\'re a practice champion!'
                                        : stats.totalQuestionsAnswered >= 50
                                        ? '🔥 You\'re on fire!'
                                        : '💪 Keep practicing!'}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Reset Confirmation Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>Reset Statistics?</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to reset all your statistics? This action cannot be undone and will reset:
                    </Typography>
                    <Box component="ul" sx={{ mt: 2 }}>
                        <li>Total questions answered</li>
                        <li>Correct answers count</li>
                        <li>Incorrect answers count</li>
                        <li>Overall accuracy</li>
                        <li>Total time spent</li>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setResetDialogOpen(false)} sx={{ fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleResetStats} color="error" variant="contained" sx={{ fontWeight: 600 }}>
                        Reset Statistics
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default QuestionStats;
